import React, { FC, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, NoMatchesFound, TabBar } from "@shared/components";
import { TabBarItem } from "@shared/components/TabBar/TabBarItem";
import { Recognition } from "@shared/models";
import { RecognitionListItem } from "@containers/Recognition/components/RecognitionListContainer/RecognitionListItem";
import { useDispatch } from "react-redux";
import { actions as RecognitionActions } from "@containers/Recognition/store";
import { RecognitionFilter } from "@shared/interfaces/RecognitionFilter.interface";

import { atFeedCap, feedCapMessage } from "../atFeedCap";
import { hasMore } from "../hasMore";
import { loadFailedMessage } from "../loadFailedMessage";
import { recognitionPersonName } from "../recognitionPersonName";
import RecognitionsEmptyScreen from "./RecognitionsEmptyScreen/RecognitionsEmptyScreen";

import "./index.scss";

interface RecognitionListProps {
  receivedRecognitions: Recognition[];
  receivedRecognitionsTotal: number;
  sentRecognitions: Recognition[];
  sentRecognitionsTotal: number;
  onListItemActionBtnClick: (recognition: Recognition, showSentTo?: boolean) => void;
  activeTab?: number;
  setActiveTab: (activeTab: number) => void;
  sentRecognitionsFilter: RecognitionFilter;
  receivedRecognitionsFilter: RecognitionFilter;
  // feed-failed-page-plan.md (A): sourced from the reducer's own failure flag (RecognitionStateType),
  // never derived here -- lets each panel tell "loaded and genuinely empty" apart from "failed to
  // load", which `receivedRecognitions.length === 0` alone cannot.
  receivedRecognitionsLoadFailed: boolean;
  sentRecognitionsLoadFailed: boolean;
  // feed-failed-page-plan.md (C'): the tab's own landed-page counter (RecognitionStateType), advanced
  // by the reducer's `.success` handler only -- see this component's own JSDoc above for why this
  // replaced both `page + 1` and a row-count derivation.
  receivedRecognitionsLoadedPages: number;
  sentRecognitionsLoadedPages: number;
  // overview-rebuild-pr4-plan.md "Source of truth for 'in flight'": feeds the two Load more buttons'
  // disabled state -- and, since overview-feed-cap-plan.md (design review, second batch, items 1/4),
  // also gates the cap line's own text and whether the button row itself renders once a tab is at the
  // cap. Must never reach TabBar's own `isLoading` prop (TabBar.tsx:26) -- TabBar renders
  // `{!isLoading ? renderContent() : null}` (TabBar.tsx:110), so passing it there would blank the
  // whole active tab body on every click.
  isLoading: boolean;
  // all-recognitions-screen-plan.md / follow-up 114: an optional band rendered INSIDE the active tab,
  // between the Received/Sent strip and the rows -- which is where the design puts the core-value
  // filter chips (frame 9439:3122 orders the bands tabs, then filters, then list).
  //
  // Rendered OUTSIDE the `rows.length ?` branch below on purpose. A filter that narrows a tab to zero
  // rows must still leave its own control on screen; drawing it inside the populated branch would hide
  // the chips exactly when the viewer needs them to get back to "All", with no way out but a reload.
  filters?: React.ReactNode;
  // pavel, 2026-09-18: "Recent recognition should show last 5 as designed." The Overview's block is a
  // fixed-length preview, not a paging list -- "See all" is how you get the rest now.
  //
  // This is a BOOLEAN, not the row count, on purpose. The count lives in one place only: the filter's
  // `limit`, which the container seeds and the api honours. Giving this component its own row number
  // too would be a second source of truth for one value, which is the exact defect this file was just
  // cured of (the received filter had a local selector AND a prop, and they disagreed).
  bounded?: boolean;
}

/**
 * The Received/Sent recognitions list (overview-rebuild-pr4-plan.md). Paging is an explicit "Load
 * more" button per tab now, not a scroll listener -- the list moved out of a fixed-height, scrolling
 * viewport into page flow (see this PR's "The scrolling viewport goes"), so scroll position can no
 * longer trigger the next page. The pagination MECHANISM survives unchanged: `loadNextPageReceived`/
 * `loadNextPageSent` still dispatch `setReceivedFilter`/`setSentFilter`, guarded by `hasMore` -- only
 * the trigger (a click, not a scroll event) and the debounce (a `disabled` attribute while `isLoading`,
 * not `useDebouncedFunction`) changed. feed-failed-page-plan.md (C'): the dispatched page is now
 * `receivedRecognitionsLoadedPages`/`sentRecognitionsLoadedPages` -- a counter the reducer advances
 * only on a successful load -- NOT `page + 1` (skips the page that failed) and NOT a row-count-derived
 * page (a short page or a local prepend could silently skip or duplicate one; see
 * RecognitionState.interface.tsx's own history of `receivedRecognitionsLoadedPages` for both
 * superseded forms).
 */
const RecognitionList: FC<RecognitionListProps> = (props) => {
  const {
    receivedRecognitions,
    receivedRecognitionsTotal,
    sentRecognitions,
    sentRecognitionsTotal,
    onListItemActionBtnClick,
    activeTab,
    setActiveTab,
    sentRecognitionsFilter,
    receivedRecognitionsFilter,
    receivedRecognitionsLoadFailed,
    sentRecognitionsLoadFailed,
    receivedRecognitionsLoadedPages,
    sentRecognitionsLoadedPages,
    isLoading,
    filters,
    bounded,
  } = props;

  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get("activeTab");
    if (tab !== null) {
      const tabNum = Number(tab);
      if (tab && !Number.isNaN(tabNum) && tabNum >= 0 && tabNum <= 1) {
        setActiveTab(tabNum);
      }
    }
  }, [searchParams, setActiveTab, setSearchParams]);

  // all-recognitions-screen-plan.md: per-tab decisions read the ACTIVE tab's filter. The auto-switch
  // guard below used to read a component-local `useSelector(getReceivedRecognitionsFilter())`, so
  // gating on *its* `core_value_id` suppressed the switch for Received only. Once core-value chips
  // became settable on the Sent tab (the dedicated All Recognitions screen), a chip there left the
  // received filter's `core_value_id` undefined, the guard stayed true, and a search that narrowed
  // Sent to zero rows yanked the viewer onto Received mid-filter.
  //
  // That selector is now gone entirely. It duplicated the `receivedRecognitionsFilter` PROP this
  // component already receives -- and already used for the retry dispatches below -- and having two
  // sources for one filter is precisely how the two tabs came to disagree. One source per filter,
  // both props, and every per-tab decision reads the tab it belongs to.
  //
  // Hoisted out of the effect rather than derived inside it so the dependency array can name the two
  // fields actually read, instead of both whole filter objects.
  const activeFilter = activeTab === 0 ? receivedRecognitionsFilter : sentRecognitionsFilter;

  useEffect(() => {
    if (activeFilter?.search && !activeFilter?.core_value_id) {
      if (activeTab === 0 && !receivedRecognitions.length && sentRecognitions.length) {
        setActiveTab(1);
        searchParams.set("activeTab", "1");
        setSearchParams(searchParams);
      }
      if (activeTab === 1 && !sentRecognitions.length && receivedRecognitions.length) {
        setActiveTab(0);
        searchParams.set("activeTab", "0");
        setSearchParams(searchParams);
      }
    }
  }, [
    activeFilter?.core_value_id,
    activeFilter?.search,
    activeTab,
    receivedRecognitions.length,
    searchParams,
    sentRecognitions,
    setActiveTab,
    setSearchParams,
  ]);

  // The guard is `hasMore`, not a restated inline expression (overview-rebuild-pr4-plan.md "One
  // predicate, not two") -- the Load more button below needs the identical check to hide itself on
  // the last page, and a second copy at render would be free to drift from this one.
  //
  // feed-failed-page-plan.md (C'): the next page to request is `receivedRecognitionsLoadedPages`, a
  // counter the reducer advances only on a successful load -- not `filter.page + 1` (skips the page
  // that failed, since nothing rolls `page` back) and not a row-count derivation (Option C, superseded:
  // a short mid-feed page re-derived the SAME page and duplicated it on append, a locally-prepended
  // row from `createRecognition.success` inflated the count and skipped a real page, and a derived page
  // of 0 could wipe the list via `is_clear: !payload.page`). A failure leaves the counter unchanged, so
  // the next click retries the SAME page; a success (short or not) always advances it by exactly one.
  // The dispatched `page` also decides replace-vs-append in the saga (`is_clear: !payload.page`), same
  // as every other filter write in this file.
  const loadNextPageReceived = useCallback(() => {
    if (hasMore(receivedRecognitions.length, receivedRecognitionsTotal)) {
      dispatch(
        RecognitionActions.setReceivedFilter({
          ...receivedRecognitionsFilter,
          page: receivedRecognitionsLoadedPages,
        }),
      );
    }
  }, [
    dispatch,
    receivedRecognitions.length,
    receivedRecognitionsFilter,
    receivedRecognitionsLoadedPages,
    receivedRecognitionsTotal,
  ]);

  const loadNextPageSent = useCallback(() => {
    if (hasMore(sentRecognitions.length, sentRecognitionsTotal)) {
      dispatch(
        RecognitionActions.setSentFilter({
          ...sentRecognitionsFilter,
          page: sentRecognitionsLoadedPages,
        }),
      );
    }
  }, [dispatch, sentRecognitions.length, sentRecognitionsFilter, sentRecognitionsLoadedPages, sentRecognitionsTotal]);

  // review A: re-dispatches the SAME request the mount effect (RecognitionListContainer.tsx) already
  // fires, bypassing `setReceivedFilter`/`setSentFilter` entirely. A filter write here is unnecessary
  // -- the filter did not change -- and would reset `receivedRecognitionsLoadedPages`/
  // `sentRecognitionsLoadedPages` to 0 for nothing if it happened to carry `page: 0`. This is also a
  // SECOND DOOR into the fetch, alongside the mount effect: it skips the
  // `setReceivedFilter`/`setSentFilter` -> effect path entirely, so any guard later added to that path
  // will not apply here.
  const retryReceived = useCallback(() => {
    dispatch(RecognitionActions.getReceivedRecognitions.request(receivedRecognitionsFilter));
  }, [dispatch, receivedRecognitionsFilter]);

  const retrySent = useCallback(() => {
    dispatch(RecognitionActions.getSentRecognitions.request(sentRecognitionsFilter));
  }, [dispatch, sentRecognitionsFilter]);

  // Design review (second batch): hoisted so the cap-line/button pair below reads each boolean once
  // instead of recomputing `atFeedCap` up to three times per tab across two now-independent render
  // branches (mirrors `isPeriodWindowed`'s own hoisting above).
  // `bounded` wins over `hasMore`. Both the Load more button AND the cap line live inside the
  // `hasMore` branch below, so switching this off removes the whole paging band in one place rather
  // than gating each control separately and leaving one behind. A bounded list genuinely HAS more rows
  // on the server -- that is what the See-all link is for -- it just does not offer to fetch them here.
  const receivedHasMore = !bounded && hasMore(receivedRecognitions.length, receivedRecognitionsTotal);
  const receivedAtFeedCap = atFeedCap(receivedRecognitions.length);
  const sentHasMore = !bounded && hasMore(sentRecognitions.length, sentRecognitionsTotal);
  const sentAtFeedCap = atFeedCap(sentRecognitions.length);

  // A bounded block is a fixed-length preview, and a preview shows neither paging controls nor totals
  // -- "See all" is where both live. pavel, 2026-09-18, marking up the Overview's tabs: "drop the
  // counters from Recent recognitions". The dedicated screen, which is not bounded, keeps them.
  const receivedTabLabel = bounded ? "Received" : `Received (${receivedRecognitionsTotal})`;
  const sentTabLabel = bounded ? "Sent" : `Sent (${sentRecognitionsTotal})`;

  return (
    <div>
      <TabBar activeTab={activeTab}>
        <TabBarItem label={receivedTabLabel}>
          {filters}
          {receivedRecognitions.length ? (
            <>
              <div className="recognition-rows">
                {receivedRecognitions.map((recognition) => (
                  <RecognitionListItem
                    key={recognition.id}
                    recognition={recognition}
                    personName={recognitionPersonName(recognition, false)}
                    onListItemActionBtnClick={onListItemActionBtnClick}
                  />
                ))}
              </div>
              {receivedRecognitionsLoadFailed ? (
                // feed-inline-failure-plan.md (design change, review-corrected): ONE message for
                // both a failed append and a failed replace -- see loadFailedMessage.ts's own JSDoc
                // for why the earlier page-based two-message discriminator was wrong. Sits ABOVE the
                // Load more row, inside the rows branch, so a failure is
                // never silent while rows are already on screen; only reachable here because
                // `receivedRecognitions.length` is already truthy in this branch, so no separate
                // `rows.length > 0` guard is needed here the way the profile's own footer needs one
                // (UserRecognitionsContainer.tsx's rows branch is NOT gated on rows.length alone).
                // Deliberately NOT nested inside the `receivedHasMore` block below: a failed REPLACE
                // can land on a list that was already fully loaded (`hasMore` false), and the
                // staleness warning must still show even though there is nothing more to load.
                // `role="status"`, not `alert` -- matches the cap line's own convention below, not
                // the zero-row panel's `alert`: the list is still usable, this is guidance. Carries
                // its OWN retry (review B): the Load more button below is gated on
                // `hasMore && (!atFeedCap || isLoading)`, which is false for a list that fits one
                // page (`hasMore` never true) or one sitting at the cap and not loading -- both real
                // states where that button would not exist at all to serve as a retry.
                <div className="feed-load-failed-row" data-testid="feed-load-failed-received" role="status">
                  {loadFailedMessage}
                  <Button
                    type="button"
                    className="retry-button"
                    data-testid="retry-received-inline"
                    disabled={isLoading}
                    onClick={retryReceived}
                  >
                    Try again
                  </Button>
                </div>
              ) : null}
              {/* overview-feed-cap-plan.md: `hasMore` must win -- a tab that is simply exhausted
                  (`!hasMore`) renders nothing, never the cap line, even once past the row cap. */}
              {receivedHasMore ? (
                <>
                  {/* Design review (second batch), item 1: the `!isLoading` gate is NOT about
                      `atFeedCap` flipping true before its rows arrive -- it reads the SAME
                      `receivedRecognitions.length` that decides whether these rows render at all, so
                      that ordering is impossible. The real reason: a period/search change re-requests
                      page 0 while this tab is still showing its PREVIOUS (stale) rows -- `isLoading`
                      true and `atFeedCap` already true off those stale rows -- and without this gate
                      the cap line would show a count that is about to be replaced. It also keeps the
                      cap line and the Load more button from both being on screen in the same render:
                      whichever one was showing before the refresh stays up (disabled, if it's the
                      button) until the new rows land. This does NOT depend on any particular ordering
                      between the reducer's `.success` handler and the shared loader clearing
                      `isLoading` -- it holds regardless of which one commits first.
                      feed-inline-failure-plan.md (D): `&& !receivedRecognitionsLoadFailed` -- an
                      authoritative-sounding "Showing the N most recent" must not sit beside a message
                      admitting what's shown may be stale/incomplete; both CAN be true at once (500
                      rows held, 600 total, a failed replace), and the failure row above already tells
                      the user everything the cap line would, so it simply yields while one is up. */}
                  {receivedAtFeedCap && !receivedRecognitionsLoadFailed ? (
                    <div className="feed-cap-row" data-testid="feed-cap-received" role="status">
                      {!isLoading ? feedCapMessage(receivedRecognitions.length, { searchAvailable: true }) : null}
                    </div>
                  ) : null}
                  {!receivedAtFeedCap || isLoading ? (
                    <div className="load-more-row">
                      <Button
                        type="button"
                        className="load-more-button"
                        data-testid="load-more-received"
                        disabled={isLoading}
                        onClick={loadNextPageReceived}
                      >
                        Load more
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          ) : receivedRecognitionsLoadFailed ? (
            // review A: a failed load renders this INSTEAD of RecognitionsEmptyScreen -- "you don't
            // have any recognitions yet" is a lie when the request never actually resolved. Takes
            // priority over the NoMatchesFound branch below too: a failed request means the real
            // "no matches" state is simply unknown, not confirmed empty.
            <div className="recognition-load-failed" data-testid="recognition-load-failed-received" role="alert">
              <p>We couldn&apos;t load your recognitions.</p>
              <Button
                type="button"
                className="retry-button"
                data-testid="retry-received"
                disabled={isLoading}
                onClick={retryReceived}
              >
                Try again
              </Button>
            </div>
          ) : receivedRecognitionsFilter?.search || receivedRecognitionsFilter?.core_value_id ? (
            <div className="no-matches-found-wrapper">
              <NoMatchesFound />
            </div>
          ) : (
            <RecognitionsEmptyScreen />
          )}
        </TabBarItem>
        <TabBarItem label={sentTabLabel}>
          {filters}
          {sentRecognitions.length ? (
            <>
              <div className="recognition-rows">
                {sentRecognitions.map((recognition) => (
                  <RecognitionListItem
                    key={recognition.id}
                    recognition={recognition}
                    personName={recognitionPersonName(recognition, true)}
                    onListItemActionBtnClick={() => onListItemActionBtnClick(recognition, true)}
                  />
                ))}
              </div>
              {sentRecognitionsLoadFailed ? (
                // Mirrors the Received branch's own inline-failure note above -- see its comment for
                // why this sits above (not nested inside) the Load more block, the `role="status"`
                // reasoning, and why it carries its own retry. Sent's own independent filter/flag.
                <div className="feed-load-failed-row" data-testid="feed-load-failed-sent" role="status">
                  {loadFailedMessage}
                  <Button
                    type="button"
                    className="retry-button"
                    data-testid="retry-sent-inline"
                    disabled={isLoading}
                    onClick={retrySent}
                  >
                    Try again
                  </Button>
                </div>
              ) : null}
              {/* overview-feed-cap-plan.md: same three-state rule as the Received branch above,
                  against Sent's own independent filter/total -- see the Received branch's own
                  comment above for the `!isLoading` reasoning (design review, second batch, item 1)
                  and the `!sentRecognitionsLoadFailed` reasoning (feed-inline-failure-plan.md, D). */}
              {sentHasMore ? (
                <>
                  {sentAtFeedCap && !sentRecognitionsLoadFailed ? (
                    <div className="feed-cap-row" data-testid="feed-cap-sent" role="status">
                      {!isLoading ? feedCapMessage(sentRecognitions.length, { searchAvailable: true }) : null}
                    </div>
                  ) : null}
                  {!sentAtFeedCap || isLoading ? (
                    <div className="load-more-row">
                      <Button
                        type="button"
                        className="load-more-button"
                        data-testid="load-more-sent"
                        disabled={isLoading}
                        onClick={loadNextPageSent}
                      >
                        Load more
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          ) : sentRecognitionsLoadFailed ? (
            // Mirrors the Received branch's own load-failed note above -- see its comment for why this
            // takes priority over both NoMatchesFound and RecognitionsEmptyScreen.
            <div className="recognition-load-failed" data-testid="recognition-load-failed-sent" role="alert">
              <p>We couldn&apos;t load your recognitions.</p>
              <Button
                type="button"
                className="retry-button"
                data-testid="retry-sent"
                disabled={isLoading}
                onClick={retrySent}
              >
                Try again
              </Button>
            </div>
          ) : sentRecognitionsFilter?.search || sentRecognitionsFilter?.core_value_id ? (
            <div className="no-matches-found-wrapper">
              <NoMatchesFound />
            </div>
          ) : (
            <RecognitionsEmptyScreen />
          )}
        </TabBarItem>
      </TabBar>
    </div>
  );
};

export default RecognitionList;
